import { Field, FieldArray, useFormikContext } from "formik";
import { get } from "lodash";
import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDeepEffect } from "../../../abstracts";
import {
  FieldButtonGroup,
  FieldDropSide,
  FieldInputDecimal,
  FieldInputNumber,
  FieldMultiSelectButtonGroup,
  SearchableList,
  HoursComponent,
  ApplyToAllStatusToggle,
} from "../../../components";
import vestingSchedules from "../../../mocks/vestingSchedules.json";
import {
  clearFieldValues,
  OPTIONS_DATA_MAPPER,
  toOptionValuesFromMapper,
  yesNoOptions,
} from "../../../utils";

const getVestScheduleDetails = (value) => {
  let scheduleDetails = ["%"];

  if (value === 1) {
    scheduleDetails = scheduleDetails.concat([0, 100, 100, 100, 100, 100]);
  } else if (value === 2) {
    scheduleDetails = scheduleDetails.concat([0, 0, 100, 100, 100, 100]);
  } else if (value === 3) {
    scheduleDetails = scheduleDetails.concat([25, 50, 75, 100, 100, 100]);
  } else if (value === 4) {
    scheduleDetails = scheduleDetails.concat([20, 40, 60, 80, 100, 100]);
  } else if (value === 5) {
    scheduleDetails = scheduleDetails.concat([0, 20, 40, 60, 80, 100]);
  }

  return scheduleDetails;
};

const vestingScheduleTableFields = [
  {
    label: "firstYear",
    value: 0,
  },
  {
    label: "secondYear",
    value: 1,
  },
  {
    label: "thirdYear",
    value: 2,
  },
  {
    label: "fourthYear",
    value: 3,
  },
  {
    label: "fifthYear",
    value: 4,
  },
];

const CommonVestingRules = ({
  fields,
  isEdit,
  isSave,
  getPlanYosDataInVesting,
  usePlanDefaultYOSIndicator,
  errorMessage,
  statusList,
}) => {
  const { values, setValues, handleChange, setFieldValue } = useFormikContext();
  const [disableYos, setDisableYos] = useState(false);
  const vestingMethod = values[fields.vestingMethod];
  useDeepEffect(() => {
    if (usePlanDefaultYOSIndicator) {
      setDisableYos(getPlanYosDataInVesting?.planYOS?.isPlanYOSApplicable);
    } else {
      setDisableYos(false);
    }
  }, [
    usePlanDefaultYOSIndicator,
    getPlanYosDataInVesting?.planYOS?.isPlanYOSApplicable,
  ]);
  const onImmediateVestingIndicatorChange = (value) => {
    const updatedValues = clearFieldValues({
      values,
      fieldsToClear: [
        fields.vestedAt,
        fields.usePlanDefaultYOSIndicator,
        fields.vestingMethod,
        fields.breakServiceApplicable,
        fields.breakServiceDefinition,
        fields.breakServiceRule,
        fields.disregardPriorService,
        fields.freezeVestingPercentage,
        fields.forfeitureTimings,
        fields.forfeitureTreatment,
        fields.defaultInvestmentId,
        fields.hoursOfService,
        fields.elapsedTime,
        fields.vestingComputationalPeriod,
        fields.serviceCreditPeriod,
        fields.vestingSchedule,
        fields.breakInServiceRules,
        fields.months,
        fields.hours,
        fields.activeEmployees,
        fields.daily,
        fields.weekly,
        fields.biWeekly,
        fields.semiMonthly,
        fields.monthly,
        fields.quarterly,
        fields.semiAnnually,
        fields.annually,
      ],
    });
    setValues({
      ...updatedValues,
      [fields.immediateVestingIndicator]: value,
      [fields.breakInServiceApplicable]: false,
      [fields.applyToAllStatuses]: true,
    });
  };

  const onVestingMethodChange = (value) => {
    let updatedValues = values;
    if (vestingMethod === 1) {
      updatedValues = clearFieldValues({
        values,
        fieldsToClear: [
          fields.elapsedTime,
          fields.vestingComputationalPeriod,
          fields.serviceCreditPeriod,
          fields.activeEmployees,
          fields.daily,
          fields.weekly,
          fields.biWeekly,
          fields.semiMonthly,
          fields.monthly,
          fields.quarterly,
          fields.semiAnnually,
          fields.annually,
        ],
      });
    } else if (vestingMethod === 2) {
      updatedValues = clearFieldValues({
        values,
        fieldsToClear: [
          fields.hoursOfService,
          fields.vestingComputationalPeriod,
          fields.serviceCreditPeriod,
          fields.activeEmployees,
          fields.daily,
          fields.weekly,
          fields.biWeekly,
          fields.semiMonthly,
          fields.monthly,
          fields.quarterly,
          fields.semiAnnually,
          fields.annually,
        ],
      });
    } else if (vestingMethod === 3) {
      updatedValues = clearFieldValues({
        values,
        fieldsToClear: [
          fields.hoursOfService,
          fields.vestingComputationalPeriod,
          fields.elapsedTime,
          fields.serviceCreditPeriod,
        ],
      });
    }

    setValues({
      ...updatedValues,
      [fields.vestingMethod]: value,
      [fields.vestingComputationalPeriod]:
        value === 1 || value === 3 ? 2 : null,
      [fields.hours]: value === 1 || value === 3 ? 500 : null,
      [fields.months]: value === 2 ? 12 : null,
      [fields.applyToAllStatuses]: true,
      hoursOfServiceForEmploymentStatus:
        value != 1
          ? values.hoursOfServiceForEmploymentStatus?.map((item) => ({
              ...item,
              hoursOfService: null,
            }))
          : values.hoursOfServiceForEmploymentStatus,
      equivalencyHoursForEmploymentStatus:
        value != 3
          ? values.equivalencyHoursForEmploymentStatus?.map((item) => ({
              ...item,
              hoursOfService: null,
            }))
          : values.equivalencyHoursForEmploymentStatus,
    });
  };
  const handleDefaultPlanYos = (value) => {
    let data = getPlanYosDataInVesting?.planYOS;
    setFieldValue(fields.usePlanDefaultYOSIndicator, value);
    setDisableYos(value);
    if (value) {
      setFieldValue([fields.vestingMethod], data?.serviceComputation);
      if (data?.serviceComputation == 1) {
        setFieldValue([fields.hoursOfService], data?.hoursOfService);
        setFieldValue(
          [fields.vestingComputationalPeriod],
          data?.computationPeriod
        );
        setFieldValue(
          [fields.serviceCreditPeriod],
          data?.planYOSServiceCreditPeriod
        );
        setFieldValue([fields.applyToAllStatuses], data?.applyToAllStatuses);
      } else if (data?.serviceComputation == 2) {
        setFieldValue([fields.elapsedTime], data?.elapsedTimeInMonths);
      } else if (data?.serviceComputation == 3) {
        setFieldValue([fields.activeEmployees], data?.activeEmployeesHours);
        setFieldValue([fields.daily], data?.dailyHours);
        setFieldValue([fields.weekly], data?.weeklyHours);
        setFieldValue([fields.biWeekly], data?.biWeeklyHours);
        setFieldValue([fields.semiMonthly], data?.semiMonthlyHours);
        setFieldValue([fields.monthly], data?.monthlyHours);
        setFieldValue([fields.quarterly], data?.quarterlyHours);
        setFieldValue([fields.semiAnnually], data?.semiAnnuallyHours);
        setFieldValue([fields.annually], data?.annuallyHours);
        setFieldValue(
          [fields.vestingComputationalPeriod],
          data?.computationPeriod
        );
        setFieldValue(
          [fields.serviceCreditPeriod],
          data?.planYOSServiceCreditPeriod
        );
        setFieldValue([fields.applyToAllStatuses], data?.applyToAllStatuses);
      }
    } else {
      setFieldValue([fields.vestingMethod], "");
      setFieldValue([fields.vestingComputationalPeriod], "");
      setFieldValue([fields.serviceCreditPeriod], "");
      setFieldValue([fields.hoursOfService], "");
      setFieldValue([fields.elapsedTime], "");
      setFieldValue([fields.activeEmployees], "");
      setFieldValue([fields.daily], "");
      setFieldValue([fields.weekly], "");
      setFieldValue([fields.biWeekly], "");
      setFieldValue([fields.semiMonthly], "");
      setFieldValue([fields.monthly], "");
      setFieldValue([fields.quarterly], "");
      setFieldValue([fields.semiAnnually], "");
      setFieldValue([fields.annually], "");
    }
  };
  const validateHoursOfService = (value) => {
    if (!value) return "Required";
    else if (value > 1000)
      return "Number of hours of service should not be greater than 1000";
    return null;
  };

  // const setTableData = (value) =>{
  //   setFieldValue(fields.vestingSchedule,value)
  //   setVestingTableValues(value)
  // }
  const setTableData = (value) => {
    let percentages = [];
    if (value > 5) {
      setValues({
        ...values,
        [fields.vestingSchedule]: value,
        [fields.firstYear]: "",
        [fields.secondYear]: "",
        [fields.thirdYear]: "",
        [fields.fourthYear]: "",
        [fields.fifthYear]: "",
      });
      return;
    }
    switch (value) {
      case 1:
        percentages = [0, 100, 100, 100, 100, 100];
        break;
      case 2:
        percentages = [0, 0, 100, 100, 100, 100];
        break;
      case 3:
        percentages = [25, 50, 75, 100, 100, 100];
        break;
      case 4:
        percentages = [20, 40, 60, 80, 100, 100];
        break;
      case 5:
        percentages = [0, 20, 40, 60, 80, 100];
        break;
      default:
        break;
    }
    setValues({
      ...values,
      [fields.vestingSchedule]: value,
      [fields.firstYear]: percentages[0],
      [fields.secondYear]: percentages[1],
      [fields.thirdYear]: percentages[2],
      [fields.fourthYear]: percentages[3],
      [fields.fifthYear]: percentages[4],
    });
  };
  const onBreakInServiceChange = (value) => {
    const updatedValues = clearFieldValues({
      values,
      fieldsToClear: [
        fields.breakInServiceRules,
        fields.disregardPrior,
        // fields.consecutiveBreaksInService,
      ],
    });
    setValues({
      ...updatedValues,
      [fields.breakInServiceApplicable]: value,
      [fields.hours]: 500,
    });
  };
  const onBreakInServiceRuleChange = (value) => {
    let x = undefined;
    if (value === 2) {
      x = get(values, fields.disregardPrior, null) ?? 5;
    }
    setValues({
      ...values,
      [fields.disregardPrior]: x,
      [fields.breakInServiceRules]: value,
    });
  };
  // const validateScheduleTable = (value, idx) => {
  //   if (!value) return "Required";
  //   else if (value > 100)
  //     return "Plan Vesting schedule percentage should be greater than or equal to previous schedule percentage and should not be greater than 100 each";
  //   else if (idx != 0) {
  //     const previousValue = get(
  //       values,
  //       vestingScheduleTableFields[idx - 1].label
  //     );
  //     if (previousValue != "" && value < previousValue)
  //       return "Plan Vesting schedule percentage should be greater than or equal to previous schedule percentage and should not be greater than 100 each";
  //   }
  //   return null;
  // };

  return (
    <>
      {/* <Field
        size="sm"
        name={fields.vestingPastPeriodComputation}
        label={"Compute YOS for past computation period"}
        options={yesNoOptions}
        selectedValue={values[fields.vestingPastPeriodComputation]}
        value={values[fields.vestingPastPeriodComputation]}
        component={FieldButtonGroup}
        disabled={isEdit && !isSave}
        onChange={(value) => {
          setFieldValue(fields.vestingPastPeriodComputation, value);
        }}
        isRequired
        noLabelTransform
      /> */}
      <Field
        size="sm"
        name={fields.immediateVestingIndicator}
        label={"Plan has 100% immediate Vesting?"}
        options={yesNoOptions}
        selectedValue={values[fields.immediateVestingIndicator]}
        value={values[fields.immediateVestingIndicator]}
        onChange={onImmediateVestingIndicatorChange}
        component={FieldButtonGroup}
        disabled={isEdit && !isSave}
        isRequired
        //validate={isAdditionalVesting && required}
      />
      {values[fields.immediateVestingIndicator] === false && (
        <>
          <Field
            size="lg"
            name={fields.vestedAt}
            label={"100% Vested At"}
            options={toOptionValuesFromMapper(OPTIONS_DATA_MAPPER.VESTED_AT)}
            selectedValue={values[fields.vestedAt]}
            onChange={(value) => {
              setFieldValue(fields.vestedAt, value);
            }}
            component={FieldMultiSelectButtonGroup}
            disabled={isEdit && !isSave}
            isRequired
            //validate={isAdditionalVesting && required}
          />
          <Field
            size="sm"
            name={fields.usePlanDefaultYOSIndicator}
            label={"Use plan default YOS Rule?"}
            options={yesNoOptions}
            selectedValue={values[fields.usePlanDefaultYOSIndicator]}
            type="text"
            autoComplete="off"
            value={values[fields.usePlanDefaultYOSIndicator]}
            onChange={handleDefaultPlanYos}
            onSelect={(value) => {
              setValues({
                ...values,
                [fields.usePlanDefaultYOSIndicator]: value,
              });
            }}
            isRequired
            component={FieldButtonGroup}
            disabled={
              (isEdit && !isSave) ||
              !getPlanYosDataInVesting?.planYOS?.isPlanYOSApplicable
            }
          />
          <Field
            size="xl"
            name={fields.vestingMethod}
            label={"Vesting Method"}
            options={toOptionValuesFromMapper(
              OPTIONS_DATA_MAPPER.VESTING_METHODS
            )}
            selectedValue={values[fields.vestingMethod]}
            value={values[fields.vestingMethod]}
            onChange={onVestingMethodChange}
            component={FieldButtonGroup}
            disabled={(isEdit && !isSave) || disableYos}
            isRequired
            //validate={isAdditionalVesting && required}
          />
          {vestingMethod === 1 && (
            <div className="d-flex flex-column">
              <ApplyToAllStatusToggle
                title={"Hours of service"}
                className={"font-hours"}
                values={values}
                setValues={setValues}
                fieldName={fields.applyToAllStatuses}
                isEdit={isEdit}
                isSave={isSave}
                disableYos={disableYos}
                hoursFieldName={fields.hoursOfService}
                hoursListFieldName={"hoursOfServiceForEmploymentStatus"}
              />
              {values[fields.applyToAllStatuses] ? (
                <Field
                  size="sm"
                  name={fields.hoursOfService}
                  // label={"Hours Of Service"}
                  type="number"
                  autoComplete="off"
                  value={values[fields.hoursOfService]}
                  onChange={handleChange}
                  disabled={(isEdit && !isSave) || disableYos}
                  component={FieldInputNumber}
                  min={0}
                  max={9999}
                  isRequired
                  //validate={isAdditionalVesting && validateHoursOfService}
                />
              ) : (
                <HoursComponent
                  handleChange={handleChange}
                  statusList={statusList}
                  valueArray={values}
                  isEdit={isEdit}
                  isSave={isSave}
                  name={"hoursOfServiceForEmploymentStatus"}
                />
              )}
            </div>
          )}
          {vestingMethod === 2 && (
            <Field
              size="sm"
              name={fields.elapsedTime}
              label={"Elapsed time (Months)"}
              autoComplete="off"
              type="number"
              value={values[fields.elapsedTime]}
              onChange={handleChange}
              disabled={(isEdit && !isSave) || disableYos}
              component={FieldInputNumber}
              min={0}
              max={99}
              isRequired
              //validate={isAdditionalVesting && validateElapsedTime}
            />
          )}
          {vestingMethod === 3 && (
            <div className="d-flex flex-column">
              <ApplyToAllStatusToggle
                title={"Hours of service"}
                className={"font-hours"}
                values={values}
                setValues={setValues}
                fieldName={fields.applyToAllStatuses}
                isEdit={isEdit}
                isSave={isSave}
                disableYos={disableYos}
                hoursFieldName={fields.activeEmployees}
                hoursListFieldName={"equivalencyHoursForEmploymentStatus"}
              />
              {values[fields.applyToAllStatuses] ? (
                <Field
                  size="sm"
                  name={fields.activeEmployees}
                  type="number"
                  autoComplete="off"
                  value={values[fields.activeEmployees]}
                  onChange={handleChange}
                  disabled={(isEdit && !isSave) || disableYos}
                  component={FieldInputNumber}
                  min={1}
                  max={9999}
                  isRequired
                />
              ) : (
                <HoursComponent
                  handleChange={handleChange}
                  statusList={statusList}
                  valueArray={values}
                  isEdit={isEdit}
                  isSave={isSave}
                  name={"equivalencyHoursForEmploymentStatus"}
                />
              )}
              <div className="mb-5rm font-hours">
                {" "}
                Equivalency hours per frequency{" "}
              </div>
              <Field
                size="sm"
                name={fields.daily}
                label={"Daily (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.daily]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
              <Field
                size="sm"
                name={fields.weekly}
                label={"Weekly (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.weekly]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
              <Field
                size="sm"
                name={fields.biWeekly}
                label={"Bi-weekly (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.biWeekly]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
              <Field
                size="sm"
                name={fields.semiMonthly}
                label={"Semi-monthly (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.semiMonthly]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
              <Field
                size="sm"
                name={fields.monthly}
                label={"Monthly (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.monthly]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
              <Field
                size="sm"
                name={fields.quarterly}
                label={"Quarterly (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.quarterly]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
              <Field
                size="sm"
                name={fields.semiAnnually}
                label={"Semi-annually (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.semiAnnually]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
              <Field
                size="sm"
                name={fields.annually}
                label={"Annually (hours)"}
                type="number"
                autoComplete="off"
                value={values[fields.annually]}
                onChange={handleChange}
                disabled={(isEdit && !isSave) || disableYos}
                component={FieldInputNumber}
                min={1}
                max={9999}
                isRequired
              />
            </div>
          )}
          {(vestingMethod === 3 || vestingMethod === 1) && (
            <>
              <Field
                name={fields.vestingComputationalPeriod}
                label={"Vesting Computational Period"}
                options={toOptionValuesFromMapper(
                  OPTIONS_DATA_MAPPER.VESTING_COMPUTATION_PERIODS
                )}
                selectedValue={values[fields.vestingComputationalPeriod]}
                value={values[fields.vestingComputationalPeriod]}
                popupContent={
                  <SearchableList
                    label="Vesting Computational Period"
                    isNotTypeAhead
                    options={toOptionValuesFromMapper(
                      OPTIONS_DATA_MAPPER.VESTING_COMPUTATION_PERIODS
                    )}
                    onSelect={(value) => {
                      setValues({
                        ...values,
                        [fields.vestingComputationalPeriod]: value,
                      });
                    }}
                    selectedValue={values[fields.vestingSchedule]}
                    value={values[fields.vestingComputationalPeriod]}
                    isRadio
                  />
                }
                component={FieldDropSide}
                disabled={(isEdit && !isSave) || disableYos}
                isRequired
                //validate={isAdditionalVesting && required}
              />
              <Field
                size="xxl"
                name={fields.serviceCreditPeriod}
                label={"Service Credit period"}
                options={toOptionValuesFromMapper(
                  OPTIONS_DATA_MAPPER.VESTING_SERVICE_CREDIT_PERIODS
                )}
                selectedValue={values[fields.serviceCreditPeriod]}
                value={values[fields.serviceCreditPeriod]}
                onChange={(value) => {
                  setValues({
                    ...values,
                    [fields.serviceCreditPeriod]: value,
                  });
                }}
                component={FieldButtonGroup}
                disabled={(isEdit && !isSave) || disableYos}
                isRequired
                //validate={isAdditionalVesting && required}
              />
            </>
          )}
          <Field
            size="md"
            label="Vesting Schedule"
            name={fields.vestingSchedule}
            value={values[fields.vestingSchedule]}
            options={vestingSchedules.data}
            disabled={isEdit && !isSave}
            popupContent={
              <SearchableList
                label="Select a Vesting Schedule"
                isNotTypeAhead
                options={vestingSchedules.data}
                onSelect={(value) => setTableData(value)}
                selectedValue={values[fields.vestingSchedule]}
                isRadio
              />
            }
            component={FieldDropSide}
            isRequired
            //validate={isAdditionalVesting && required}
          />
          {values[fields.vestingSchedule] && (
            <div>
              <FieldArray name="vestingtable">
                <Row>
                  {values[fields.vestingSchedule] === 6 ? (
                    <Col md="5" className="vesting-table editable">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>1</th>
                            <th>2</th>
                            <th>3</th>
                            <th>4</th>
                            <th>5</th>
                            <th>6</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>%</td>
                            {vestingScheduleTableFields.map((item, idx) => (
                              <td>
                                <Field
                                  size="xs"
                                  name={
                                    idx == 0
                                      ? fields.firstYear
                                      : idx == 1
                                      ? fields.secondYear
                                      : idx == 2
                                      ? fields.thirdYear
                                      : idx == 3
                                      ? fields.fourthYear
                                      : fields.fifthYear
                                  }
                                  value={values[item.label]}
                                  onChange={handleChange}
                                  disabled={isEdit && !isSave}
                                  component={FieldInputDecimal}
                                  isRequired
                                  // validate={(value) => {
                                  //   if (isAdditionalVesting) {
                                  //     //return validateScheduleTable(value, idx);
                                  //     return validateData(values)
                                  //   }
                                  // }}
                                />
                              </td>
                            ))}
                            <td>100</td>
                          </tr>
                        </tbody>
                      </table>
                    </Col>
                  ) : (
                    <Col md="5" className="vesting-table">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>1</th>
                            <th>2</th>
                            <th>3</th>
                            <th>4</th>
                            <th>5</th>
                            <th>6</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {getVestScheduleDetails(
                              values[fields.vestingSchedule]
                            ).map((perc, index) => {
                              return <td key={perc + index}>{perc}</td>;
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </Col>
                  )}
                </Row>
              </FieldArray>
              <div>
                {errorMessage?.map((val) => (
                  <p
                    style={{
                      color: "Red",
                      fontSize: "0.75rem",
                      // fontFamily: "Poppins",
                      fontWeight: "400",
                    }}
                  >
                    {val}
                  </p>
                ))}
              </div>
            </div>
          )}
          <h4 className="ft-18 py-3">Break In Service</h4>
          <Field
            size="sm"
            name={fields.breakInServiceApplicable}
            label={"Break In Service applicable?"}
            options={yesNoOptions}
            selectedValue={values[fields.breakInServiceApplicable]}
            value={values[fields.breakInServiceApplicable]}
            onChange={onBreakInServiceChange}
            component={FieldButtonGroup}
            disabled={isEdit && !isSave}
            isRequired
            //validate={isAdditionalVesting && required}
          />

          {values[fields.breakInServiceApplicable] === true && (
            <div className="vesting-bis">Break In Service Definition</div>
          )}
          {/* {values[fields.breakInServiceApplicable] === true &&
            (vestingMethod === 1 || vestingMethod === 3) && } */}

          {values[fields.breakInServiceApplicable] === true &&
            (vestingMethod === 2 ? (
              <Field
                size="sm"
                name={fields.months}
                label={"Months"}
                autoComplete="off"
                type="number"
                value={values[fields.months]}
                onChange={handleChange}
                disabled={isEdit && !isSave}
                component={FieldInputNumber}
                min={0}
                max={99}
                isRequired
                //validate={isAdditionalVesting && validateElapsedTime}
              />
            ) : (
              <Field
                size="sm"
                name={fields.hours}
                label={"Hours"}
                type="number"
                autoComplete="off"
                value={values[fields.hours]}
                onChange={handleChange}
                disabled={isEdit && !isSave}
                component={FieldInputNumber}
                min={0}
                max={9999}
                isRequired
                //validate={isAdditionalVesting && validateHoursOfService}
              />
            ))}
          {values[fields.breakInServiceApplicable] === true && (
            <>
              <Field
                size="lg"
                name={fields.breakInServiceRules}
                label={"Break In Service Rule"}
                options={toOptionValuesFromMapper(
                  OPTIONS_DATA_MAPPER.VESTING_BREAK_IN_SERVICE_RULE
                )}
                selectedValue={values[fields.breakInServiceRules]}
                onChange={onBreakInServiceRuleChange}
                component={FieldButtonGroup}
                disabled={isEdit && !isSave}
                isRequired
                //validate={isAdditionalVesting && required}
              />
            </>
          )}

          {values[fields.breakInServiceApplicable] === true &&
            values[fields.breakInServiceRules] === 2 && (
              <div className="d-flex width-20px">
                <div className="vesting-bis-left">
                  Disregard prior service at the{" "}
                </div>
                <Field
                  size="sm"
                  name={fields.disregardPrior}
                  label={""}
                  autoComplete="off"
                  type="number"
                  value={values[fields.disregardPrior]}
                  onChange={handleChange}
                  disabled={isEdit && !isSave}
                  component={FieldInputNumber}
                  min={0}
                  max={9}
                  isRequired
                  //validate={isAdditionalVesting && validateElapsedTime}
                />

                <div className="vesting-bis-right">
                  Consecutive Break In Service{" "}
                </div>
              </div>
            )}
        </>
      )}
    </>
  );
};

export default CommonVestingRules;
